import { IStackItemStyles, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { HoverCallout } from 'components/Utils/HoverCallout';
import { Integration } from 'models/integrations/integration';
import { IntegrationRun, IntegrationRunStatus } from 'models/integrations/integrationRun';
import KPIData from 'models/kpi/kpiData';
import { TaskColors } from 'models/tasks/task';
import { useContext, useMemo, useState } from 'react';
import { apiGetKPIData } from 'services/Api/kpiService';
import { apiRequest } from 'services/Auth/authConfig';
import { TaskViewScheduleLevel } from '../../Tasks/View/TasksView';
import { IntegrationEvidenceReportModal } from '../IntegrationEvidenceReportModal';
import { StatBox } from '../IntegrationRenderHelpers';
import { IntegrationRunPeriodCallout } from './IntegrationRunPeriodCallout';

interface IIntegrationRunPeriodSummaryProps {
  integration: Integration;
  runs: IntegrationRun[];
  level: TaskViewScheduleLevel;
  period: number;
  onUpdateRun?: (run: IntegrationRun) => void;
}

const getRunResultDate = (run: IntegrationRun): number => {
  return (run.data?.created ?? run.completed ?? run.dueAt).getTime();
};

export const IntegrationRunPeriodSummary = (props: IIntegrationRunPeriodSummaryProps) => {
  const appContext = useContext(AppContext);
  const [selectedEvidence, setSelectedEvidence] = useState<KPIData | undefined>(undefined);
  const theme = getTheme();

  const nrOfRuns = useMemo(
    () => props.runs.filter((r) => r.status !== IntegrationRunStatus.pending).length,
    [props.runs],
  );

  const nrOfAccepted = useMemo(() => props.runs.filter((r) => r.isAccepted()).length, [props.runs]);

  const nrOfRejectedNotFixed = useMemo(() => props.runs.filter((r) => r.isRejectectAndNotFixed()).length, [props.runs]);

  const latestResultRun = useMemo(
    () =>
      props.runs
        .filter((r) => r.status === IntegrationRunStatus.completed && r.kpiDataId !== undefined)
        .sort((a, b) => getRunResultDate(b) - getRunResultDate(a))[0],
    [props.runs],
  );

  const openLatestEvidenceReport = async () => {
    try {
      if (!latestResultRun?.kpiDataId) return;

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const fullData = await apiGetKPIData(props.integration.kpi, latestResultRun.kpiDataId, accessToken);

      setSelectedEvidence(fullData);
    } catch (err) {
      appContext.setError(err);
    }
  };

  const getBackgroundColor = (hasNotAccepted: boolean): string => {
    return hasNotAccepted
      ? appContext.useDarkMode
        ? TaskColors.DarkOverDeadline
        : TaskColors.OverDeadline
      : appContext.useDarkMode
      ? TaskColors.DarkCompleted
      : TaskColors.Completed;
  };

  const getRunSummaryStyleProps = (hasNotAccepted: boolean): IStackItemStyles => {
    const backgroundColor = getBackgroundColor(hasNotAccepted);

    return {
      root: {
        background: backgroundColor,
        color: appContext.useDarkMode ? theme.palette.themeLight : theme.palette.themeSecondary,
        height: 22,
        width: props.level === TaskViewScheduleLevel.Month ? 40 : 70,
        borderRadius: 4,
        borderColor: theme.palette.themeSecondary,
        cursor: 'pointer',
      },
    };
  };

  if (nrOfRuns === 0) return null;

  //a SkillControl check (phase 20R): SkillControl reprocesses its own runs, and its evidence is not a typed integration
  //evidence the report can parse
  const isExternalCheck = props.integration.externalCheck !== undefined;

  const percentage = Math.round((nrOfAccepted / nrOfRuns) * 100);
  const text = `${percentage}%`;

  return (
    <>
      <HoverCallout
        openDelayMs={200}
        closeDelayMs={500}
        calloutLeaveBehavior="delay"
        renderContent={() => (
          <IntegrationRunPeriodCallout
            integration={props.integration}
            runs={props.runs}
            onUpdateRun={isExternalCheck ? undefined : props.onUpdateRun}
            onOpenLatestEvidenceReport={latestResultRun && !isExternalCheck ? openLatestEvidenceReport : undefined}
          />
        )}
      >
        <StatBox text={text} styles={getRunSummaryStyleProps(nrOfRejectedNotFixed > 0)} />
      </HoverCallout>
      {selectedEvidence && (
        <IntegrationEvidenceReportModal
          isOpen={selectedEvidence !== undefined}
          integration={props.integration}
          kpiData={selectedEvidence}
          onClose={() => setSelectedEvidence(undefined)}
        />
      )}
    </>
  );
};
